import {
  AnalyticsBrowser,
  Analytics,
  type CDNSettings,
  type InitOptions,
  type MiddlewareFunction,
} from '@segment/analytics-next';
import { createCollectorPlugin } from './collectorPlugin';
export type AnalyticsInstance = Analytics;

type AnalyticsConfig = {
  segmentWriteKey: string;
  environment: string;
  superProperties: SuperProperties;
  enabled: boolean;
  debug: boolean;
  domains: string[];
  /** In-house collector host (e.g. `t.<zone>`); events are copied there when set. */
  collectorHost: string;
  /** Called when the SDK or the collector plugin fails to load. */
  onLoadError: (error: unknown) => void;
};
type SuperProperties = Record<string, unknown>;

// Singleton instance
let analyticsInstance: Analytics | null = null;
let initPromise: Promise<Analytics> | null = null;

let config: Partial<AnalyticsConfig> = { enabled: true, debug: false };

const enabledListeners = new Set<() => void>();

/** Runs `listener` each time tracking switches on (e.g. the viewer accepts cookies). */
export const onAnalyticsEnabled = (listener: () => void) => {
  enabledListeners.add(listener);
  return () => {
    enabledListeners.delete(listener);
  };
};

export const setAnalyticsConfig = (next: Partial<AnalyticsConfig>) => {
  const wasEnabled = config.enabled;
  config = {
    ...config,
    ...next,
    // Deep-merge superProperties so multiple providers (e.g. separate React trees)
    // don't clobber each other's values
    superProperties: { ...config.superProperties, ...next.superProperties },
  };
  // If consent was just granted, clear the cached no-op so getAnalytics()
  // re-initializes with real Segment on the next call.
  if (!wasEnabled && config.enabled) {
    analyticsInstance = null;
    initPromise = null;
    enabledListeners.forEach((listener) => listener());
  }
  debugLog('Config updated', config);
};

const skipIfDisabled = (
  _target: unknown,
  _propertyKey: string,
  descriptor: PropertyDescriptor,
) => {
  const original = descriptor.value as (...args: unknown[]) => unknown;
  descriptor.value = function (...args: unknown[]) {
    if (!config.enabled) return Promise.resolve();
    return original.apply(this, args);
  };
  return descriptor;
};

const debugLog = (label: string, ...args: unknown[]) => {
  if (config.debug) {
    console.debug(`[Analytics] ${label}`, ...args);
  }
};

const logIfDebug =
  (label: string) =>
  (_target: unknown, _propertyKey: string, descriptor: PropertyDescriptor) => {
    const original = descriptor.value as (...args: unknown[]) => unknown;
    descriptor.value = function (...args: unknown[]) {
      debugLog(label, ...args);
      return original.apply(this, args);
    };
    return descriptor;
  };

// also possible to implmement with a Segment plugin or middlware
const mergeSuperPropertiesAt =
  (index: number) =>
  (_target: unknown, _propertyKey: string, descriptor: PropertyDescriptor) => {
    const original = descriptor.value as (...args: unknown[]) => unknown;
    descriptor.value = function (...args: unknown[]) {
      const merged = [...args];
      merged[index] = withSuperProperties(
        (args[index] as Record<string, unknown>) ?? {},
      );
      return original.apply(this, merged);
    };
    return descriptor;
  };

class AnalyticsWrapper {
  constructor(private readonly inner: Analytics) {}

  @skipIfDisabled
  @logIfDebug('track')
  @mergeSuperPropertiesAt(1)
  track(...args: Parameters<Analytics['track']>) {
    return this.inner.track(...args);
  }

  @skipIfDisabled
  @logIfDebug('identify')
  identify(...args: Parameters<Analytics['identify']>) {
    return this.inner.identify(...args);
  }

  @skipIfDisabled
  @logIfDebug('page')
  @mergeSuperPropertiesAt(2)
  page(...args: Parameters<Analytics['page']>) {
    return this.inner.page(...args);
  }

  // Not skipped when disabled: clearing ids after a cookie reject must still work
  @logIfDebug('reset')
  reset(...args: Parameters<Analytics['reset']>) {
    return this.inner.reset(...args);
  }

  @skipIfDisabled
  @logIfDebug('group')
  group(...args: Parameters<Analytics['group']>) {
    return this.inner.group(...args);
  }
}
/**
 * Load Segment; if its settings can't be fetched from cdn.segment.com (e.g. blocked),
 * retry with built-in ones so events still reach the collector. Remote destinations
 * (e.g. GA4) need the CDN and stay off in that case, and so does Segment.io: its API
 * is usually blocked too, and its retry queue would keep undeliverable events in
 * localStorage.
 */
const loadSegment = async (
  writeKey: string,
  options: InitOptions,
  collectorHost: string | undefined,
) => {
  try {
    return await AnalyticsBrowser.load({ writeKey }, options);
  } catch (error) {
    if (!collectorHost) throw error;
    console.warn(
      '[Analytics] Segment CDN unreachable, loading with built-in settings:',
      error,
    );
    const cdnSettings: CDNSettings = {
      integrations: { 'Segment.io': { apiKey: writeKey } },
    };
    // Only the name-matched Segment.io destination is skipped; the collector plugin still runs
    return AnalyticsBrowser.load(
      { writeKey, cdnSettings },
      { ...options, integrations: { 'Segment.io': false } },
    );
  }
};

/**
 * Get the analytics instance (lazy initialization)
 */
export const getAnalytics = (): Promise<Analytics> => {
  if (!config.segmentWriteKey) {
    throw new Error('Write key is not set');
  }
  if (initPromise !== null) return initPromise;
  const {
    segmentWriteKey: writeKey,
    superProperties,
    enabled,
    debug,
    domains,
    collectorHost,
    onLoadError,
  } = config;
  initPromise = (async () => {
    if (!enabled || !writeKey) {
      console.debug('[Analytics] Analytics is disabled, using no-op fallback');
      analyticsInstance = createNoOpAnalytics();
      return analyticsInstance;
    }

    try {
      // Segment's cookie.domain option only accepts a single domain
      const options: InitOptions = {
        cookie: {
          // Send ajs_* cookies over HTTPS only (pentest finding #6)
          secure: true,
          ...(domains?.[0] && { domain: domains[0] }),
        },
      };
      const [analytics] = await loadSegment(writeKey, options, collectorHost);
      await analytics.addSourceMiddleware(stripSensitiveParamsMiddleware);
      if (collectorHost) {
        // A collector failure must not take Segment down with it
        try {
          // Source middleware runs before destinations, so the collector gets stripped URLs too
          await analytics.register(createCollectorPlugin(collectorHost));
        } catch (error) {
          console.warn(
            '[Analytics] Failed to load the collector plugin:',
            error,
          );
          onLoadError?.(error);
        }
      }
      analyticsInstance = analytics;

      if (debug) {
        console.debug('[Analytics] Segment initialized', {
          writeKey: writeKey.substring(0, 8) + '...',
          superProperties,
        });
      }
      const wrapper = new AnalyticsWrapper(analyticsInstance);
      return {
        ...analyticsInstance,
        // override the methods to use the wrapper with logs and skip if disabled
        track: wrapper.track.bind(wrapper),
        identify: wrapper.identify.bind(wrapper),
        page: wrapper.page.bind(wrapper),
        reset: wrapper.reset.bind(wrapper),
        group: wrapper.group.bind(wrapper),
      } as Analytics;
    } catch (error) {
      console.warn(
        '[Analytics] Failed to load Segment, using no-op fallback:',
        error,
      );
      // Without a collector this is usually just an ad blocker, as before; not worth a report
      if (collectorHost) onLoadError?.(error);
      analyticsInstance = createNoOpAnalytics();
      return analyticsInstance;
    }
  })();

  return initPromise;
};

// Query params that must never reach Segment (e.g. the email-link PIN)
const SENSITIVE_QUERY_PARAMS = ['pin_code'];
const URL_FIELDS = ['url', 'search', 'referrer'] as const;

/** Removes sensitive query params from a full URL or a `?a=b` search string. */
const stripSensitiveParams = (value: string): string => {
  if (!SENSITIVE_QUERY_PARAMS.some((param) => value.includes(param))) {
    return value;
  }
  if (value.startsWith('?')) {
    const params = new URLSearchParams(value);
    SENSITIVE_QUERY_PARAMS.forEach((param) => params.delete(param));
    const search = params.toString();
    return search ? `?${search}` : '';
  }
  try {
    const url = new URL(value);
    SENSITIVE_QUERY_PARAMS.forEach((param) => url.searchParams.delete(param));
    return url.toString();
  } catch {
    return value;
  }
};

const stripSensitiveParamsMiddleware: MiddlewareFunction = ({
  payload,
  next,
}) => {
  const { context, properties } = payload.obj;
  // Page context is attached to every event; page events also copy it into properties
  [context?.page, properties].forEach((target) => {
    if (!target) return;
    URL_FIELDS.forEach((field) => {
      const value = target[field];
      if (typeof value === 'string') {
        target[field] = stripSensitiveParams(value);
      }
    });
  });
  next(payload);
};

/**
 * Merge super properties with event properties
 */
const withSuperProperties = <T extends Record<string, unknown>>(
  props: T,
): T & SuperProperties => ({
  ...config.superProperties,
  ...props,
});

export const registerSuperProperties = (props: Record<string, unknown>) => {
  config.superProperties = { ...config.superProperties, ...props };
};

export const clearSuperProperties = () => {
  config.superProperties = {};
};

/**
 * Check if analytics is enabled
 * enabled by default
 */
export const isEnabled = (): boolean => config.enabled ?? true;

/**
 * Check if debug mode is active
 */
export const isDebug = (): boolean => config.debug ?? false;

/**
 * Reset analytics state (for testing only)
 */
export const resetAnalytics = () => {
  analyticsInstance = null;
  initPromise = null;
  config = { enabled: false, debug: false };
};

/**
 * Create a no-op analytics instance for disabled environments
 */
function createNoOpAnalytics(): Analytics {
  const noOp = () => Promise.resolve();
  return {
    track: noOp,
    identify: noOp,
    page: noOp,
    reset: noOp,
    group: noOp,
    user: () => ({
      id: () => null,
      anonymousId: () => null,
      traits: () => ({}),
    }),
  } as unknown as Analytics;
}
